import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUY_UNITS, compatibleUnits, eur, unitLabel, currencySymbol } from "@/lib/format";
import { t } from "@/i18n";

const NEW = "__new__";

export default function LineRow({ line, idx, ingredients, cost, onChange, onRemove }) {
  const ing = ingredients.find((i) => i.id === line.ingredient_id);
  const baseUnit = ing?.unit || line.new_ingredient?.unit || "kg";
  const set = (patch) => onChange({ ...line, ...patch });
  const setNew = (patch) => set({ new_ingredient: { ...line.new_ingredient, ...patch } });

  const pick = (v) => {
    if (v === NEW) return set({ ingredient_id: null, new_ingredient: { name: "", unit: "kg", unit_cost: 0, category: "Autre" }, unit: "g" });
    const target = ingredients.find((i) => i.id === v);
    set({ ingredient_id: v, new_ingredient: null, unit: compatibleUnits(target.unit).includes(line.unit) ? line.unit : target.unit });
  };

  return (
    <div className="rounded-lg border bg-background/40 p-3" data-testid={`recipe-line-${idx}`}>
      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-12 sm:col-span-5">
          <Select value={line.ingredient_id || NEW} onValueChange={pick}>
            <SelectTrigger data-testid={`recipe-line-ingredient-select-${idx}`}><SelectValue placeholder={t("common.ingredient")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW}>{t("lineRow.nouvel_ingredient")}</SelectItem>
              {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input className="num col-span-4 sm:col-span-2" inputMode="decimal" value={line.quantity} onChange={(e) => set({ quantity: e.target.value })} data-testid={`recipe-line-qty-${idx}`} />
        <div className="col-span-4 sm:col-span-2">
          <Select value={line.unit} onValueChange={(v) => set({ unit: v })}>
            <SelectTrigger data-testid={`recipe-line-unit-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>{compatibleUnits(baseUnit).map((u) => <SelectItem key={u} value={u}>{unitLabel(u)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="num col-span-3 text-right text-sm font-semibold sm:col-span-2" data-testid={`recipe-line-cost-${idx}`}>{eur(cost)}</p>
        <Button variant="ghost" size="icon" className="col-span-1" onClick={onRemove} data-testid={`recipe-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {line.new_ingredient && (
        <div className="mt-2 grid grid-cols-12 gap-2 rounded-md bg-amber-500/5 p-2">
          <Input className="col-span-12 sm:col-span-5" placeholder={t("lineRow.nom_du_nouvel_ingredient")} value={line.new_ingredient.name} onChange={(e) => setNew({ name: e.target.value })} data-testid={`recipe-line-new-name-${idx}`} />
          <div className="col-span-6 sm:col-span-3">
            <Select value={line.new_ingredient.unit} onValueChange={(v) => { setNew({ unit: v }); if (!compatibleUnits(v).includes(line.unit)) set({ unit: v, new_ingredient: { ...line.new_ingredient, unit: v } }); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{BUY_UNITS.map((u) => <SelectItem key={u} value={u}>{t("lineRow.achat_au")} {unitLabel(u)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="relative col-span-6 sm:col-span-4">
            <Input className="num pr-14" inputMode="decimal" value={line.new_ingredient.unit_cost} onChange={(e) => setNew({ unit_cost: e.target.value })} data-testid={`recipe-line-new-cost-${idx}`} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{currencySymbol()}/{unitLabel(line.new_ingredient.unit)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
